import { safeSessionStorage } from './ssr-helper';

const CACHE_PREFIX = 'app_cache_';

function simpleHash(obj) {
  try {
    const str = JSON.stringify(obj);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString();
  } catch (error) {
    console.warn('Error generating hash:', error);
    return '';
  }
}

function deepEqual(obj1, obj2) {
  try {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  } catch (error) {
    console.warn('Error comparing objects:', error);
    return false;
  }
}

export function getCachedData(key) {
  try {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    const cached = safeSessionStorage.getItem(cacheKey);
    
    if (!cached) {
      return null;
    }

    const { data, timestamp, ttl } = JSON.parse(cached);
    const now = Date.now();
    
    if (ttl && (now - timestamp) > ttl) {
      safeSessionStorage.removeItem(cacheKey);
      return null;
    }

    return data;
  } catch (error) {
    console.warn(`Error getting cached data for key ${key}:`, error);
    return null;
  }
}

export function setCachedData(key, data, ttl = null) {
  try {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    const cacheEntry = {
      data,
      timestamp: Date.now(),
      ttl,
      hash: simpleHash(data),
    };
    
    safeSessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    return true;
  } catch (error) {
    console.warn(`Error setting cached data for key ${key}:`, error);
    return false;
  }
}

export function updateCacheIfChanged(key, newData, ttl = null) {
  try {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    const cached = safeSessionStorage.getItem(cacheKey);
    
    if (!cached) {
      setCachedData(key, newData, ttl);
      return { updated: true, data: newData };
    }

    const { data: cachedData, hash: cachedHash } = JSON.parse(cached);
    const newHash = simpleHash(newData);
    
    if (cachedHash === newHash) {
      // Los datos no han cambiado, actualizar solo el timestamp
      const cacheEntry = {
        data: cachedData,
        timestamp: Date.now(),
        ttl,
        hash: cachedHash,
      };
      safeSessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
      return { updated: false, data: cachedData };
    }
    
    if (deepEqual(cachedData, newData)) {
      const cacheEntry = {
        data: cachedData,
        timestamp: Date.now(),
        ttl,
        hash: newHash,
      };
      safeSessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
      return { updated: false, data: cachedData };
    }
    
    setCachedData(key, newData, ttl);
    return { updated: true, data: newData };
  } catch (error) {
    console.warn(`Error updating cache for key ${key}:`, error);
    setCachedData(key, newData, ttl);
    return { updated: true, data: newData };
  }
}

export function hasCachedData(key) {
  const data = getCachedData(key);
  return data !== null;
}

export function removeCachedData(key) {
  try {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    safeSessionStorage.removeItem(cacheKey);
    return true;
  } catch (error) {
    console.warn(`Error removing cached data for key ${key}:`, error);
    return false;
  }
}

export function clearAllCache() {
  try {
    let count = 0;
    const keys = Object.keys(sessionStorage);
    
    keys.forEach((key) => {
      if (key.startsWith(CACHE_PREFIX)) {
        safeSessionStorage.removeItem(key);
        count++;
      }
    });
    
    return count;
  } catch (error) {
    console.warn('Error clearing cache:', error);
    return 0;
  }
}

export function getCacheInfo(key) {
  try {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    const cached = safeSessionStorage.getItem(cacheKey);
    
    if (!cached) {
      return null;
    }

    const { timestamp, ttl, hash } = JSON.parse(cached);
    const now = Date.now();
    const age = now - timestamp;
    const isExpired = ttl ? age > ttl : false;
    const timeRemaining = ttl ? Math.max(0, ttl - age) : null;
    
    return {
      key,
      timestamp,
      ttl,
      hash,
      age,
      isExpired,
      timeRemaining,
    };
  } catch (error) {
    console.warn(`Error getting cache info for key ${key}:`, error);
    return null;
  }
}
